// src/hooks/useUserTickets.js
//
// Upcoming rooms the wallet holds a ticket to.
// Unique job: wallet → room ids (from room_tickets). Room shaping is delegated
// to roomService — the SAME _dbToUi/_hydrateRooms path RoomPage/Discover use —
// so seat math, pricing, creator join, and column names come from one source.
//
// PERF PASS:
//   • WAS: one getRoomById per ticket = N room queries + N creator fetches
//     (10 tickets ≈ 21 round-trips), re-run in full by the auto-refresh.
//     NOW: one room_tickets query + one batched getRoomsByIds query
//     (creator hydration deduped/cached inside the service). 2 round-trips
//     total, regardless of ticket count.
//   • Ended/cancelled rooms are filtered SERVER-SIDE — their base64
//     thumbnails were previously downloaded just to be discarded.
//   • Auto-refresh is SILENT — `loading` flips only on the initial load
//     (or wallet change), so consumers keyed on it never flash a spinner
//     every 60s.
//   • Stale-response guard: an older in-flight fetch can't overwrite a
//     newer one (e.g. rapid wallet switches).

import { useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { roomService } from '../services/RoomService';
import { isRoomPast } from '../utils/roomUtils';

// Server-side allowlist (inverse of the old ENDED_STATUSES denylist):
// only these statuses are upcoming/joinable, so only these are fetched.
const UPCOMING_STATUSES = ['live', 'scheduled'];

export function useUserTickets(walletAddress, { autoRefreshMs = 0 } = {}) {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const seqRef = useRef(0);

  const fetchTickets = useCallback(async ({ silent = false } = {}) => {
    const seq = ++seqRef.current;

    if (!walletAddress) {
      setTickets([]);
      return;
    }

    if (!silent) setLoading(true);
    setError(null);

    try {
      // 1. The only thing unique to this hook: which rooms has this wallet ticketed?
      const { data: rows, error: tErr } = await supabase
        .from('room_tickets')
        .select('room_id')
        .eq('wallet_address', walletAddress.toLowerCase());
      if (tErr) throw tErr;
      if (seq !== seqRef.current) return; // stale — a newer fetch superseded us

      const roomIds = [...new Set((rows || []).map((r) => r.room_id).filter(Boolean))];
      if (roomIds.length === 0) {
        setTickets([]);
        return;
      }

      // 2. ONE batched query, upcoming statuses only — shaping identical
      //    to Discover/RoomPage via the service's shared hydration path.
      const rooms = await roomService.getRoomsByIds(roomIds, {
        statuses: UPCOMING_STATUSES,
      });
      if (seq !== seqRef.current) return;

      // 3. Drop rooms whose scheduled time is already well past — nothing
      //    ever flips stale `scheduled` rows to `ended` server-side, so a
      //    status-only filter would show dead events forever (P1.2).
      //    Then: soonest first, live pinned to the front.
      const upcoming = rooms.filter((r) => !isRoomPast(r)).sort((a, b) => {
        if (a.status === 'live' && b.status !== 'live') return -1;
        if (b.status === 'live' && a.status !== 'live') return 1;
        const ta = a.scheduledAt ? new Date(a.scheduledAt).getTime() : Infinity;
        const tb = b.scheduledAt ? new Date(b.scheduledAt).getTime() : Infinity;
        return ta - tb;
      });

      setTickets(upcoming);
    } catch (err) {
      if (seq !== seqRef.current) return;
      console.error('[useUserTickets] fetch failed:', err);
      setError(err);
    } finally {
      if (seq === seqRef.current && !silent) setLoading(false);
    }
  }, [walletAddress]);

  // Initial load (and wallet changes) — the only non-silent fetch.
  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  // Background refresh — silent: updates in place, never flips `loading`.
  useEffect(() => {
    if (!autoRefreshMs || autoRefreshMs <= 0) return;
    const id = setInterval(() => fetchTickets({ silent: true }), autoRefreshMs);
    return () => clearInterval(id);
  }, [fetchTickets, autoRefreshMs]);

  return { tickets, count: tickets.length, loading, error, refetch: fetchTickets };
}